import React, { useCallback, useEffect, useMemo, useState } from 'react'
import classnames from 'classnames'

import { analytics, apiManager, constants } from 'helpers'
import { trackEventViewed } from 'helpers/analyticsHelper'
import {
  useAuthState,
  useBreakpoints,
  useEventSignUp,
  useLayoutHeaderTransparency,
  useQuery,
} from 'hooks'
import useAppBanner from 'hooks/use-app-banner'
import { getEventImage } from 'helpers/event'
import openNotificationWithMsg from 'helpers/Notifications'

import { AppDownloadBanner, AuthModal, BannerBackButton, PrimaryButton } from 'components'
import TicketBox from './components/TicketBox'
import EventInfo from './components/EventInfo'
import MobileCta from './components/MobileCta'
import TicketModal from './components/TicketModal'
import TicketDetailsModal from './components/TicketDetailsModal'

import defaultEventImage from 'assets/images/eventPageImage.jpg'

const {
  ROUTES: { LANDING },
  AUTH_STATUS: { LOGGING_IN },
} = constants

const EventPage = ({ history, match, location: routeLocation }) => {
  useLayoutHeaderTransparency(true)
  const { md, lg } = useBreakpoints()
  const { status, user } = useAuthState()
  const signupForEvent = useEventSignUp()
  const [showTicketModal, setShowTicketModal] = useState(false)
  const [showVerificationModal, setShowVerificationModal] = useState(false)
  const [authCompleted, setAuthCompleted] = useState(false)
  const [showTicketDetails, setShowTicketDetails] = useState(false)
  const { showAppBanner, AppBanner } = useAppBanner()

  const eventId = match.params.event_id

  const { data, refetch: refetchEvent } = useQuery(
    'events',
    async () =>
      user?.completedOnboarding
        ? await apiManager.getEvent(eventId)
        : await apiManager.getEventPublicInfo(eventId),
    {
      enabled: status !== LOGGING_IN,
    }
  )
  const event = data?.event

  const isFromDiscoverPage = routeLocation?.state?.prevPath === LANDING

  const eventImage = useMemo(() => (event ? getEventImage(event, md) : ''), [event, md])

  useEffect(() => event && trackEventViewed(event, 'Detail Page'), [event])

  const hideAuthModal = useCallback(() => setShowVerificationModal(false), [])

  const handleReserveTicket = useCallback(
    event => {
      if (user?.id) {
        signupForEvent(event, {
          onSuccess: () => {
            refetchEvent()
            setShowTicketDetails(true)
            openNotificationWithMsg('success', 'APPROVED — YOU ARE ON THE LIST')
          },
        })
      } else {
        analytics.segmentTrack('Anonymous Clicked Accept Invite Button', { eventId: event.id })
        setShowVerificationModal(true)
      }
    },
    [user, signupForEvent, refetchEvent]
  )

  useEffect(() => {
    if (user?.waitlist) {
      analytics.segmentTrack('User Accepted Invite', { eventId })
    }
  }, [eventId, user])

  useEffect(() => {
    if (user?.id && authCompleted && event?.series.length > 1) {
      setAuthCompleted(false)
      setTimeout(() => setShowTicketModal(true), 1000)
    }
  }, [authCompleted, event, user?.id])

  // event not found
  if (data?.statusCode === 404) {
    return (
      <ErrorContainer>
        <p className="bg-background">
          Sorry, we could not find the event or it does not match your current settings/preferences.
        </p>
        <PrimaryButton className="h-12 w-45 text-base mt-6" onClick={() => history.push(LANDING)}>
          Take me home
        </PrimaryButton>
      </ErrorContainer>
    )
  } else if (data?.error) {
    return (
      <ErrorContainer>
        <p>Sorry, something went wrong. Please try again later!</p>
      </ErrorContainer>
    )
  }

  if (!event) return null

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'http://schema.org',
            '@type': 'Event',
            name: event.title,
            startDate: event.startTime,
            eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
            organizer: {
              '@type': 'Organization',
              name: 'Teleport Dating',
              url: 'https://teleport.co',
            },
            location: event.location,
            url: window.location.href,
            image: [event.image],
            description: event.description,
          }),
        }}
      />
      <div>
        <div className="bg-background my-0 pt-1">
          <div className="w-full relative h-96 lg:h-120 m-0">
            <div
              className="absolute inset-0 w-full h-52"
              style={{
                background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.0001) 0%, #000000 99.26%)',
                transform: 'rotate(-180deg)',
              }}
            />

            {isFromDiscoverPage && (
              <BannerBackButton to={routeLocation?.state?.prevPath} text="Discover" />
            )}
            <img
              className="w-full h-full object-cover object-top rounded-none md:object-center"
              src={eventImage || defaultEventImage}
              alt="event"
            />
            <div
              className="absolute h-64 w-full left-0 -bottom-2"
              style={{
                background:
                  'linear-gradient(to bottom, rgba(0, 0, 0, 0.0001) 0%, rgba(0, 0, 0, 0.6) 30%,  rgba(0, 0, 0, 0.9) 60%,  rgba(0, 0, 0, 0.995) 80%, #000000 99.26%)',
              }}
            />
          </div>
          <div className="relative w-full flex flex-col justify-between items-start m-auto p-0 lg:flex-row lg:px-6">
            <EventInfo event={event} />
            <div
              className={classnames(
                'w-full p-4 bg-background rounded-xl mt-4 md:mt-5 lg:mt-0 lg:bg-white lg:bg-opacity-8 lg:p-7 lg:right-8 lg:w-2/5 xl:w-4/12',
                {
                  'sticky-mobile lg:sticky': !showAppBanner,
                }
              )}
            >
              {lg ? (
                <TicketBox
                  event={event}
                  history={history}
                  user={user}
                  onReserve={handleReserveTicket}
                />
              ) : (
                <MobileCta
                  event={event}
                  onReserve={handleReserveTicket}
                  history={history}
                  user={user}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <AppDownloadBanner />
      <AppBanner />
      <AuthModal
        // event={event}
        show={showVerificationModal}
        onClose={hideAuthModal}
        onComplete={() => {
          hideAuthModal()
          // setAuthCompleted(true)
        }}
      />
      <TicketModal
        show={showTicketModal}
        event={event}
        history={history}
        user={user}
        onReserve={handleReserveTicket}
        onClose={() => setShowTicketModal(false)}
      />
      {user && (
        <TicketDetailsModal
          show={showTicketDetails}
          onClose={() => setShowTicketDetails(false)}
          user={user}
          event={event}
        />
      )}
    </>
  )
}

const ErrorContainer = ({ children }) => (
  <div className="mt-28 text-white text-lg text-center flex flex-col items-center h-32">
    {children}
  </div>
)

export default EventPage
